import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import SEOHead from './components/SEOHead';
import AuthPage from './pages/AuthPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Calculator from './pages/Calculator';
import AdminPage from './pages/AdminPage';
import CommissionsPage from './pages/CommissionsPage';
import SAVPage from './pages/SAVPage';
import FraisBureauPage from './pages/FraisBureauPage';
import ChargesPage from './pages/ChargesPage';
import CompteCommercialPage from './pages/CompteCommercialPage';
import FraisVehiculesPage from './pages/FraisVehiculesPage';
import FraisVehiculeFormPage from './pages/FraisVehiculeFormPage';
import FraisMutualiseFormPage from './pages/FraisMutualiseFormPage';
import ControleCGPage from './pages/ControleCGPage';
import TvaPage from './pages/TvaPage';
import OptimisationPage from './pages/OptimisationPage';
import CoherencePage from './pages/CoherencePage';
import ScanFacturesPage from './pages/ScanFacturesPage';
import ControleFacturesPage from './pages/ControleFacturesPage';
import PageLogicielMarchand from './pages/seo/PageLogicielMarchand';
import PageCalculMarge from './pages/seo/PageCalculMarge';
import PageGestionFrais from './pages/seo/PageGestionFrais';
import PageSuiviMarge from './pages/seo/PageSuiviMarge';
import InvitePage from './pages/InvitePage';
import AppLayout from './components/AppLayout';
import MobileLayout from './components/MobileLayout';
import MobileDashboard from './pages/MobileDashboard';
import MobileCommissions from './pages/MobileCommissions';
import MobileDossiers from './pages/MobileDossiers';
import MobileProfile from './pages/MobileProfile';
import { Loader2 } from 'lucide-react';
import './App.css';

const INVOICE_TEAM = ['super_admin', 'admin', 'admin_agence', 'responsable', 'assistant'];
const INVOICE_ONLY = ['assistant'];

function ProtectedRoute({ children, adminOnly = false, roles = null }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!user || user === false) {
        return <Navigate to="/landing" replace />;
    }

    const allowed = roles || (adminOnly ? ['super_admin', 'admin', 'admin_agence'] : null);
    if (INVOICE_ONLY.includes(user.role) && !(roles || []).includes(user.role)) return <Navigate to="/controle-factures" replace />;
    if (allowed && !allowed.includes(user.role)) {
        return <Navigate to={INVOICE_TEAM.includes(user.role) ? '/controle-factures' : '/dashboard'} replace />;
    }

    return <AppLayout><SEOHead noindex />{children}</AppLayout>;
}

function MobileRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!user || user === false) {
        return <Navigate to="/landing" replace />;
    }

    // Admin users should not access mobile-only routes
    if (['super_admin', 'admin', 'admin_agence'].includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <MobileLayout><SEOHead noindex />{children}</MobileLayout>;
}

function SmartRoute({ children, mobileComponent, adminOnly = false }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!user || user === false) {
        return <Navigate to="/landing" replace />;
    }

    const isAdmin = ['super_admin', 'admin', 'admin_agence'].includes(user.role);
    if (INVOICE_ONLY.includes(user.role)) return <Navigate to="/controle-factures" replace />;

    if (adminOnly && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // Commercial users get mobile layout
    if (!isAdmin && mobileComponent) {
        return <MobileLayout><SEOHead noindex />{mobileComponent}</MobileLayout>;
    }

    return <AppLayout><SEOHead noindex />{children}</AppLayout>;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (user && user !== false) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/landing" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/logiciel-marchand-automobile" element={<PageLogicielMarchand />} />
            <Route path="/calcul-marge-vehicule-occasion" element={<PageCalculMarge />} />
            <Route path="/gestion-frais-vehicules" element={<PageGestionFrais />} />
            <Route path="/suivi-marge-par-vehicule" element={<PageSuiviMarge />} />
            <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Smart routes: admin gets desktop, commercial gets mobile */}
            <Route path="/dashboard" element={<SmartRoute mobileComponent={<MobileDashboard />}><Dashboard /></SmartRoute>} />
            <Route path="/commissions" element={<SmartRoute mobileComponent={<MobileCommissions />}><CommissionsPage /></SmartRoute>} />

            {/* Calculator: commercial gets mobile layout without bottom nav */}
            <Route path="/calculator" element={<SmartRoute mobileComponent={<Calculator />}><Calculator /></SmartRoute>} />
            <Route path="/calculator/:id" element={<SmartRoute mobileComponent={<Calculator />}><Calculator /></SmartRoute>} />

            {/* Mobile-only routes for commercials */}
            <Route path="/mes-dossiers" element={<MobileRoute><MobileDossiers /></MobileRoute>} />
            <Route path="/profil" element={<MobileRoute><MobileProfile /></MobileRoute>} />

            {/* Admin-only routes */}
            <Route path="/compte-commercial/:id" element={<ProtectedRoute adminOnly><CompteCommercialPage /></ProtectedRoute>} />
            <Route path="/sav" element={<ProtectedRoute roles={['super_admin', 'admin', 'admin_agence', 'assistant']}><SAVPage /></ProtectedRoute>} />
            <Route path="/frais-bureau" element={<ProtectedRoute adminOnly><FraisBureauPage /></ProtectedRoute>} />
            <Route path="/frais-vehicules" element={<ProtectedRoute roles={INVOICE_TEAM}><FraisVehiculesPage /></ProtectedRoute>} />
            <Route path="/frais-vehicules/nouveau" element={<ProtectedRoute adminOnly><FraisVehiculeFormPage /></ProtectedRoute>} />
            <Route path="/frais-vehicules/:id/edit" element={<ProtectedRoute adminOnly><FraisVehiculeFormPage /></ProtectedRoute>} />
            <Route path="/frais-mutualises/nouveau" element={<ProtectedRoute adminOnly><FraisMutualiseFormPage /></ProtectedRoute>} />
            <Route path="/frais-mutualises/:id/edit" element={<ProtectedRoute adminOnly><FraisMutualiseFormPage /></ProtectedRoute>} />
            <Route path="/controle-cg" element={<ProtectedRoute adminOnly><ControleCGPage /></ProtectedRoute>} />
            <Route path="/tva" element={<ProtectedRoute adminOnly><TvaPage /></ProtectedRoute>} />
            <Route path="/optimisation" element={<ProtectedRoute adminOnly><OptimisationPage /></ProtectedRoute>} />
            <Route path="/coherence" element={<ProtectedRoute adminOnly><CoherencePage /></ProtectedRoute>} />
            <Route path="/scan-factures" element={<ProtectedRoute roles={INVOICE_TEAM}><ScanFacturesPage /></ProtectedRoute>} />
            <Route path="/controle-factures" element={<ProtectedRoute roles={INVOICE_TEAM}><ControleFacturesPage /></ProtectedRoute>} />
            <Route path="/charges" element={<ProtectedRoute adminOnly><ChargesPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/landing" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <HelmetProvider>
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
